/**
 * Schemas e tipos comuns a todos os módulos.
 *
 * Papel: aqui ficam o formato de erro da API, os parâmetros de listagem e os
 * campos que se repetem em várias entidades — identificador, texto, e-mail,
 * telefone e data.
 *
 * Como usar: um campo que exista em mais de um módulo entra por aqui, e não por
 * uma validação escrita dentro do módulo. O schema que o formulário usa para
 * conferir o que foi digitado é o mesmo que a Function usa para conferir o que
 * chegou pela rede, de modo que as duas pontas não têm como discordar sobre o
 * que é um dado válido; duas cópias da mesma regra divergem no dia em que uma
 * delas for corrigida.
 *
 * Datas trafegam sempre em ISO 8601, e o formato brasileiro existe apenas na
 * renderização. Os campos de formato fixo — CPF, placa, CEP, renavam, chassi,
 * dinheiro — moram em `formatos.ts`, ao lado destes.
 */
import { z } from 'zod';

/**
 * Identificador de registro.
 *
 * Todas as tabelas usam UUID gerado pelo banco, opaco e imutável. Um
 * identificador tirado do conteúdo — o nome de uma pessoa, a placa de um carro —
 * muda quando alguém corrige a grafia, e todo vínculo feito sobre ele quebra
 * junto.
 */
export const idSchema = z.uuid({ message: 'Identificador inválido.' });

/**
 * Texto obrigatório, já sem espaços nas pontas.
 *
 * Aplica `trim` antes de medir o comprimento, de modo que um campo com apenas
 * espaços seja rejeitado como vazio em vez de aceito como preenchido.
 *
 * @param rotulo - Nome do campo, usado na mensagem mostrada ao usuário.
 * @param maximo - Comprimento máximo permitido. Padrão de 200 caracteres.
 * @returns Schema de string obrigatória com as mensagens já em português.
 */
export function textoObrigatorio(rotulo: string, maximo = 200) {
  return z
    .string()
    .trim()
    .min(1, { message: `${rotulo} é obrigatório.` })
    .max(maximo, { message: `${rotulo} deve ter no máximo ${maximo} caracteres.` });
}

/**
 * Texto opcional que normaliza vazio para `null`.
 *
 * Campo de formulário não preenchido chega como string vazia, e gravar `''` no
 * banco cria uma terceira categoria além de "preenchido" e "ausente" — que
 * depois obriga toda consulta a testar os dois casos. Aqui o vazio vira `null`
 * na entrada, e o banco tem só uma forma de representar ausência.
 *
 * @param maximo - Comprimento máximo permitido. Padrão de 200 caracteres.
 * @returns Schema que aceita texto, vazio ou ausente, e devolve texto ou `null`.
 */
export function textoOpcional(maximo = 200) {
  return z
    .string()
    .trim()
    .max(maximo, { message: `Deve ter no máximo ${maximo} caracteres.` })
    .nullish()
    .transform((valor) => (valor == null || valor === '' ? null : valor));
}

/**
 * E-mail opcional, guardado em minúsculas.
 *
 * O campo não é obrigatório porque boa parte dos contatos — pedreiro,
 * jardineiro, fornecedor local — só tem telefone. Exigir o endereço faria quem
 * cadastra um deles inventar um valor para conseguir salvar, e o cadastro
 * passaria a conter e-mail que não existe.
 *
 * A normalização para minúsculas acontece antes da validação, de modo que
 * `Nome.Sobrenome@Exemplo.com.br` e `nome.sobrenome@exemplo.com.br` não entrem
 * como dois contatos diferentes. Quando há conteúdo, o formato é exigido; vazio vira
 * `null`, nunca string vazia.
 */
export const emailOpcionalSchema = z
  .string()
  .trim()
  .toLowerCase()
  .nullish()
  .transform((valor) => (valor == null || valor === '' ? null : valor))
  .refine((valor) => valor === null || z.email().safeParse(valor).success, {
    message: 'E-mail inválido.',
  });

/**
 * Telefone brasileiro opcional, guardado como dez ou onze dígitos.
 *
 * Os números chegam em toda forma imaginável — `(11) 99999.9999`,
 * `11 99999-9999`, `(11) 9.9999.9999`, `+55 11 99999-9999` —, cada um conforme
 * quem digitou ou de onde foi colado. Guardar apenas os dígitos é o que torna a
 * busca previsível e deixa a apresentação como decisão da tela, feita por
 * `formatarTelefone`.
 *
 * O código de país é removido quando vem junto, porque `5511999999999` e
 * `11999999999` são o mesmo telefone e guardá-los diferentes faria o mesmo
 * contato aparecer duas vezes numa busca. Dez dígitos é fixo com DDD, onze é
 * celular com DDD; qualquer outra contagem é erro de digitação e é recusada
 * na hora, e não descoberta no dia em que alguém tentar ligar.
 */
export const telefoneOpcionalSchema = z
  .string()
  .trim()
  .nullish()
  .transform((valor) => {
    if (valor == null) return null;
    let digitos = valor.replace(/\D/g, '');
    if (digitos.length > 11 && digitos.startsWith('55')) digitos = digitos.slice(2);
    return digitos === '' ? null : digitos;
  })
  .refine((valor) => valor === null || valor.length === 10 || valor.length === 11, {
    message: 'Telefone deve ter 10 dígitos (fixo) ou 11 (celular), com DDD.',
  });

/**
 * Data em ISO 8601, sem hora.
 *
 * Existe um formato só, e a conversão para `DD/MM/AAAA` acontece na
 * renderização. Aceitar a forma brasileira aqui, "só neste campo", faria duas
 * grafias da mesma data conviverem no banco, e ordenar ou comparar por data
 * passaria a dar resultado errado sem erro nenhum. `2026-02-31` é recusado: o
 * `z.iso.date` confere os dias de cada mês, inclusive o 29 de fevereiro.
 */
export const dataSchema = z.iso.date({ message: 'Data inválida (esperado AAAA-MM-DD).' });

/**
 * Data opcional em ISO 8601, que normaliza vazio para `null`.
 *
 * Existe porque a ausência de data é comum e significa coisas diferentes
 * conforme o campo: o RG não vence, o documento que ninguém achou não tem data
 * nenhuma, e o plano de saúde pode não ter prazo. As três ausências entram como
 * `null`, e é o que faz o aviso de vencimento simplesmente pular a linha em vez
 * de tratar uma data inventada.
 *
 * A forma é `.nullish()` depois do `union`, e não `z.undefined()` dentro dele:
 * um `union` com `z.undefined()` **não** torna a chave omitível, e o corpo que
 * não trouxesse o campo seria recusado com `expected nonoptional`.
 */
export const dataOpcionalSchema = z
  .union([dataSchema, z.literal('')])
  .nullish()
  .transform((valor) => (valor == null || valor === '' ? null : valor));

/** Parâmetros aceitos por qualquer rota de listagem. */
export const parametrosListagemSchema = z.object({
  /** Página solicitada, começando em 1. */
  pagina: z.coerce.number().int().min(1).default(1),
  /** Quantos registros por página. O teto de 200 protege o tempo de resposta. */
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
  /** Busca textual livre. Cada módulo decide em quais colunas procurar. */
  busca: z.string().trim().max(120).optional(),
});

export type ParametrosListagem = z.infer<typeof parametrosListagemSchema>;

/**
 * Monta o schema de uma resposta paginada para um item qualquer.
 *
 * Toda listagem da API responde nesta forma. Ter um envelope único significa
 * que o componente de tabela e o hook de listagem são escritos uma vez e
 * servem para os onze módulos, em vez de cada um inventar o seu formato.
 *
 * @param itemSchema - Schema de um item da lista.
 * @returns Schema do envelope, com os itens e os dados de paginação.
 */
export function respostaPaginadaSchema<T extends z.ZodType>(itemSchema: T) {
  return z.object({
    itens: z.array(itemSchema),
    total: z.number().int().min(0),
    pagina: z.number().int().min(1),
    porPagina: z.number().int().min(1),
  });
}

/** Formato único de erro devolvido pela API. */
export const respostaErroSchema = z.object({
  erro: z.object({
    /** Código estável, para o front decidir o que fazer sem ler a mensagem. */
    codigo: z.string(),
    /** Mensagem em português, adequada para mostrar ao usuário. */
    mensagem: z.string(),
    /** Erros por campo, quando a falha veio de validação. */
    campos: z.record(z.string(), z.array(z.string())).optional(),
  }),
});

export type RespostaErro = z.infer<typeof respostaErroSchema>;

/**
 * Códigos de erro que a API pode devolver.
 *
 * São estáveis por contrato: o front decide o comportamento a partir do código,
 * nunca do texto da mensagem — que pode ser reescrito a qualquer momento sem
 * que isso seja considerado uma quebra.
 */
export const CODIGOS_ERRO = {
  NAO_AUTENTICADO: 'NAO_AUTENTICADO',
  SEM_PERMISSAO: 'SEM_PERMISSAO',
  NAO_ENCONTRADO: 'NAO_ENCONTRADO',
  DADOS_INVALIDOS: 'DADOS_INVALIDOS',
  CONFLITO: 'CONFLITO',
  ERRO_INTERNO: 'ERRO_INTERNO',
} as const;

export type CodigoErro = (typeof CODIGOS_ERRO)[keyof typeof CODIGOS_ERRO];
